import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useEffect, useCallback, useState, useMemo } from "react";
import {
  DataTable,
  buildListMasterTableColumns,
} from "../../../components/data-table";
import { ListMasterPageLayout } from "../../../components/list-master";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { toast } from "sonner";
import {
  designationMasterAPI,
  type DesignationMaster,
  type DesignationMasterCreatePayload,
} from "../../../services/api";
import { CommonPagination } from "../../../components/CommonPagination";

type DesignationFormState = {
  name: string;
  order_by: string;
  is_active: boolean;
};

const initialFormState: DesignationFormState = {
  name: "",
  order_by: "1",
  is_active: true,
};

function getErrorMessage(
  err: unknown,
  fallback: string = "Failed to save designation"
) {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as {
        response?: {
          data?: { message?: string; detail?: string; error?: string };
        };
      }
    ).response;
    return (
      response?.data?.message ??
      response?.data?.detail ??
      response?.data?.error ??
      (err as unknown as Error)?.message ??
      fallback
    );
  }
  return (err as Error)?.message ?? fallback;
}

export default function DesignationPage() {
  const [designations, setDesignations] = useState<DesignationMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<DesignationFormState>(initialFormState);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editing, setEditing] = useState<DesignationMaster | null>(null);
  const [deleting, setDeleting] = useState<DesignationMaster | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const resetForm = () => {
    setForm(initialFormState);
    setEditing(null);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await designationMasterAPI.listPaginated({
        page: currentPage,
        page_size: pageSize,
        search: searchTerm.trim() || undefined,
      });
      const list = response.data ?? response.results ?? [];
      const count = response.count ?? response.total_count ?? list.length;
      const computedTotalPages =
        response.total_pages ?? Math.ceil(count / pageSize);

      setDesignations(Array.isArray(list) ? list : []);
      setTotalItems(count);
      setTotalPages(computedTotalPages);
    } catch {
      toast.error("Failed to load designations");
      setDesignations([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 300);

    return () => clearTimeout(timer);
  }, [fetchList]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Designation name is required");
      return;
    }

    if (form.order_by.trim() === "") {
      toast.error("Order is required");
      return;
    }

    const orderValue = Number(form.order_by);
    if (!Number.isFinite(orderValue)) {
      toast.error("Order must be a valid number");
      return;
    }

    setSubmitLoading(true);
    try {
      const payload: DesignationMasterCreatePayload = {
        name: form.name.trim(),
        order_by: orderValue,
        is_active: form.is_active,
      };

      if (editing) {
        await designationMasterAPI.update(editing.id, payload);
        toast.success("Designation updated successfully");
      } else {
        await designationMasterAPI.create(payload);
        toast.success("Designation created successfully");
      }

      setDialogOpen(false);
      resetForm();
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err)));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) {
      return;
    }

    setDeleteLoading(true);
    try {
      await designationMasterAPI.delete(deleting.id);
      toast.success("Designation deleted successfully");
      setDeleting(null);
      if (designations.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchList();
      }
    } catch (err: unknown) {
      toast.error(String(getErrorMessage(err, "Failed to delete designation")));
    } finally {
      setDeleteLoading(false);
    }
  };

  const openEdit = useCallback((designation: DesignationMaster) => {
    setEditing(designation);
    setForm({
      name: designation.name,
      order_by: String(designation.order_by ?? 1),
      is_active: designation.is_active ?? true,
    });
    setDialogOpen(true);
  }, []);

  const tableColumns = useMemo(
    () =>
      buildListMasterTableColumns<DesignationMaster>({
        currentPage,
        pageSize,
        onEdit: openEdit,
        onDelete: setDeleting,
        showCode: false,
        extraColumns: [
          {
            id: "order",
            label: "Order",
            width: 80,
            renderCell: row => row.order_by ?? "—",
          },
        ],
      }),
    [currentPage, pageSize, openEdit]
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground">
          Designation Master
        </h1>
        <p className="text-muted-foreground">
          Manage designations shown across the app
        </p>
      </div>
      <ListMasterPageLayout
        tableId="list-master-designation"
        tableColumns={tableColumns}
        listTitle={`Designations (${totalItems})`}
        searchPlaceholder="Search designations..."
        searchTerm={searchTerm}
        onSearchChange={v => {
          setSearchTerm(v);
          setCurrentPage(1);
        }}
        headerActions={
          <Dialog
            open={dialogOpen}
            onOpenChange={open => {
              setDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                onClick={() => {
                  resetForm();
                  setDialogOpen(true);
                }}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Create new
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editing ? "Edit Designation" : "Create New Designation"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="designation-name">Name *</Label>
                  <Input
                    id="designation-name"
                    value={form.name}
                    onChange={e =>
                      setForm(prev => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Designation name"
                  />
                </div>
                <div>
                  <Label htmlFor="designation-order">Order *</Label>
                  <Input
                    id="designation-order"
                    type="number"
                    min="0"
                    value={form.order_by}
                    onChange={e =>
                      setForm(prev => ({ ...prev, order_by: e.target.value }))
                    }
                    placeholder="Display order"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="designation-active"
                    checked={form.is_active}
                    onCheckedChange={checked =>
                      setForm(prev => ({ ...prev, is_active: checked }))
                    }
                  />
                  <Label htmlFor="designation-active">Active</Label>
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                    disabled={submitLoading}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSave} disabled={submitLoading}>
                    {submitLoading ? (
                      <CustomLoader size="xs" />
                    ) : editing ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        }
      >
        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <CustomLoader size="md" />
          </div>
        ) : (
          <DataTable
            embedded
            hideToolbar
            tableId="list-master-designation"
            columns={tableColumns}
            data={designations}
            getRowKey={d => d.id}
            emptyMessage={
              totalItems === 0 && !searchTerm.trim()
                ? "No designations yet. Create one to get started."
                : "No designations found matching your search."
            }
          />
        )}

        {totalItems > 0 && (
          <div className="mt-4 border-t pt-4">
            <CommonPagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalCount={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={size => {
                setPageSize(size);
                setCurrentPage(1);
              }}
            />
          </div>
        )}
      </ListMasterPageLayout>

      <Dialog
        open={!!deleting}
        onOpenChange={open => !open && setDeleting(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Designation</DialogTitle>
          </DialogHeader>
          {deleting && (
            <>
              <p className="text-sm text-muted-foreground">
                Are you sure you want to delete <strong>{deleting.name}</strong>
                ?
              </p>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleting(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
